import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { callBackend } from '../../utils/api';
import { isNetworkFailure, NETWORK_FAILURE_MESSAGE } from '../../utils/networkFailure';
import {
  secondaryActionButtonSx,
  secondarySectionFrameSx,
  secondarySectionHeaderSx,
} from '../../styles/secondaryMsiStyles';
import { uppercaseLabel } from '../../styles/typography';
import { formatUpdated, systemCredentialsQuery } from './SystemCredentialsPanel';
import {
  dateRangeLabel,
  describeRegeneration,
  describeRegenerationError,
  describeRun,
  entryOrigin,
  entryStatus,
  FIRST_YEAR,
  formatUsd,
  groupEntries,
  latestCompletedMonth,
  MONTH_NAMES,
  monthBounds,
  monthLabel,
  pad,
  periodLabel,
  remainingBudget,
  RUN_KINDS,
  RUN_STATUSES,
  runCost,
  runPhase,
  shortMonthLabel,
} from './marketCalendarAdmin';

const ADMIN_KEY = ['admin', 'market-calendar'];
const ENTRIES_KEY = [...ADMIN_KEY, 'entries'];
const RUNS_KEY = [...ADMIN_KEY, 'job-runs'];
const REGENERATE_KEY = [...ADMIN_KEY, 'regenerate'];
// What users see in Compare & Explore: the calendar cards and the stress library.
const USER_VIEW_KEYS = [['market_calendar'], ['stress_library']];
const RUN_LIMIT = 25;
const ENTRY_LIMIT = 100;
// While a month regenerates, its run row is re-read to show how far it has got.
const PROGRESS_POLL_MS = 5000;
// A finished regeneration's result stays for a later visit to this tab.
const RESULT_KEEP_MS = 30 * 60_000;
const KEY_MISSING_STATUSES = new Set(['empty', 'not_set_up', 'not_configured']);

function errorText(error) {
  if (isNetworkFailure(error)) return NETWORK_FAILURE_MESSAGE;
  return error?.message || 'The request failed. Try again.';
}

const sectionHeaderSx = {
  px: { xs: 1.2, md: 1.5 },
  py: 1,
  display: 'flex',
  alignItems: { xs: 'flex-start', sm: 'center' },
  justifyContent: 'space-between',
  gap: 1,
  flexWrap: 'wrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: (theme) => alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.16 : 0.42),
  ...secondarySectionHeaderSx,
};
const smallButtonSx = { textTransform: 'none', fontWeight: 700, minHeight: 30, ...secondaryActionButtonSx };
const secondaryTextSx = { fontSize: '0.74rem', color: 'text.secondary' };

function Section({ title, caption = null, action = null, bodySx = null, children }) {
  return (
    <Box component="section" aria-label={title} sx={secondarySectionFrameSx}>
      <Box sx={sectionHeaderSx}>
        <Box sx={{ display: 'grid', gap: 0.25, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>{title}</Typography>
          {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
        </Box>
        {action}
      </Box>
      <Box sx={{ px: { xs: 1.2, md: 1.5 }, py: 1.2, display: 'grid', gap: 1.1, ...bodySx }}>{children}</Box>
    </Box>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  caption: PropTypes.node,
  action: PropTypes.node,
  bodySx: PropTypes.object,
  children: PropTypes.node,
};

function RefreshButton({ label, onClick }) {
  return (
    <Button
      size="small"
      variant="outlined"
      aria-label={label}
      startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
      onClick={onClick}
      sx={smallButtonSx}
    >
      Refresh
    </Button>
  );
}

RefreshButton.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func.isRequired };

function Loading({ text }) {
  return (
    <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
      <CircularProgress size={16} />
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{text}</Typography>
    </Box>
  );
}

Loading.propTypes = { text: PropTypes.string.isRequired };

function LoadError({ text, onRetry }) {
  return (
    <Alert severity="error" action={<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}>
      {text}
    </Alert>
  );
}

LoadError.propTypes = { text: PropTypes.string.isRequired, onRetry: PropTypes.func.isRequired };

// When the shown version got its status, in plain words.
function entryWhen(entry) {
  if (entry.status === 'published') return `published ${formatUpdated(entry.published_at) || ''}`.trim();
  if (entry.status === 'superseded') return `replaced ${formatUpdated(entry.status_changed_at) || ''}`.trim();
  if (entry.hidden_reason === 'admin') return `hidden ${formatUpdated(entry.status_changed_at) || ''}`.trim();
  return `stored ${formatUpdated(entry.created_at) || ''}`.trim();
}

const entryMeta = (entry) => [
  `Version ${entry.version}`,
  entryOrigin(entry),
  `${entry.verified_event_count} of ${entry.event_count} events verified`,
  entryWhen(entry),
].filter(Boolean).join(' · ');

function EventList({ events }) {
  if (!events.length) return <Typography sx={secondaryTextSx}>This version has no events.</Typography>;
  return (
    <Box sx={{ display: 'grid', gap: 0.6 }}>
      <Typography sx={secondaryTextSx}>Only verified events reach users.</Typography>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.6 }}>
        {events.map((event) => (
          <Box
            component="li"
            key={event.event_id}
            sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 0.75, alignItems: 'start' }}
          >
            {event.verified ? (
              <CheckCircleOutlineRoundedIcon color="success" titleAccess="Verified" sx={{ fontSize: 16, mt: 0.2 }} />
            ) : (
              <HighlightOffRoundedIcon color="disabled" titleAccess="Not verified" sx={{ fontSize: 16, mt: 0.2 }} />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.78rem' }}>
                <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>{dateRangeLabel(event.start, event.end)}</Box>
                {event.text}
              </Typography>
              {!event.verified && event.check_note && (
                <Typography sx={{ ...secondaryTextSx, fontSize: '0.72rem' }}>{event.check_note}</Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

EventList.propTypes = { events: PropTypes.arrayOf(PropTypes.object).isRequired };

function ToggleButton({ open, onClick, children }) {
  return (
    <Button
      size="small"
      aria-expanded={open}
      onClick={onClick}
      endIcon={open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
      sx={{ ...smallButtonSx, minHeight: 26, px: 0.75 }}
    >
      {children}
    </Button>
  );
}

ToggleButton.propTypes = { open: PropTypes.bool.isRequired, onClick: PropTypes.func.isRequired, children: PropTypes.node };

function EntryGroup({ group, busy, onSetHidden }) {
  const [showEvents, setShowEvents] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);
  // A period whose only versions were replaced shows its newest one.
  const entry = group.current || group.earlier[0];
  const earlier = group.current ? group.earlier : group.earlier.slice(1);
  const status = entryStatus(entry);
  const events = Array.isArray(entry.events) ? entry.events : [];
  const hiddenByChecks = entry.status === 'hidden' && entry.hidden_reason !== 'admin';
  return (
    <Box
      component="article"
      aria-label={`${periodLabel(entry)}: ${entry.title}`}
      sx={{ ...secondarySectionFrameSx, p: 1.2, display: 'grid', gap: 0.6 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'grid', gap: 0.25, minWidth: 0 }}>
          <Typography sx={uppercaseLabel({ fontSize: '0.66rem', color: 'text.secondary', letterSpacing: '0.06em' })}>
            {periodLabel(entry)}
          </Typography>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700 }}>{entry.title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip size="small" color={status.color} label={status.label} sx={{ fontWeight: 700 }} />
          {entry.status === 'published' && (
            <Button size="small" variant="outlined" disabled={busy} onClick={() => onSetHidden(entry, true)} sx={smallButtonSx}
              aria-label={`Hide ${entry.title}`}>
              Hide
            </Button>
          )}
          {entry.status === 'hidden' && entry.hidden_reason === 'admin' && (
            <Button size="small" variant="outlined" disabled={busy} onClick={() => onSetHidden(entry, false)} sx={smallButtonSx}
              aria-label={`Unhide ${entry.title}`}>
              Unhide
            </Button>
          )}
        </Box>
      </Box>
      <Typography sx={secondaryTextSx}>{entryMeta(entry)}</Typography>
      <Typography sx={{ fontSize: '0.78rem' }}>{entry.summary}</Typography>
      {hiddenByChecks && (
        <Typography sx={{ ...secondaryTextSx, color: 'warning.main' }}>
          The checks hid this version because fewer than 2 events verified. Regenerate the month to publish a new one.
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <ToggleButton open={showEvents} onClick={() => setShowEvents((open) => !open)}>
          {`Events (${events.length})`}
        </ToggleButton>
        {earlier.length > 0 && (
          <ToggleButton open={showEarlier} onClick={() => setShowEarlier((open) => !open)}>
            {`Earlier versions (${earlier.length})`}
          </ToggleButton>
        )}
      </Box>
      {showEvents && <EventList events={events} />}
      {showEarlier && (
        <Box component="ul" aria-label="Earlier versions" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.6 }}>
          {earlier.map((version) => (
            <Box component="li" key={version.entry_id} sx={{ display: 'grid', gap: 0.15 }}>
              <Typography sx={{ fontSize: '0.76rem', fontWeight: 600 }}>{version.title}</Typography>
              <Typography sx={{ ...secondaryTextSx, fontSize: '0.72rem' }}>{entryMeta(version)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

EntryGroup.propTypes = {
  group: PropTypes.shape({
    current: PropTypes.object,
    earlier: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  busy: PropTypes.bool.isRequired,
  onSetHidden: PropTypes.func.isRequired,
};

// The month's entries in every status. Keyed by month by its parent, so a
// notice never outlives the month it is about.
function MonthEntries({ month }) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState(null);
  const name = monthLabel(month);
  const { from, to } = monthBounds(month);
  const entries = useQuery({
    queryKey: [...ENTRIES_KEY, month],
    queryFn: ({ signal }) => callBackend(
      `admin/market-calendar/entries?from=${from}&to=${to}&status=published,hidden,superseded&limit=${ENTRY_LIMIT}`,
      'GET', null, { signal },
    ),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const setHidden = useMutation({
    mutationFn: ({ entry, hide }) => callBackend(
      `admin/market-calendar/entries/${entry.entry_id}/${hide ? 'hide' : 'unhide'}`, 'POST',
    ),
    onSuccess: (_result, { entry, hide }) => setNotice({
      severity: 'success',
      text: hide ? `Hidden. Users no longer see “${entry.title}”.` : `Unhidden. Users see “${entry.title}” again.`,
    }),
    onError: (error, { hide }) => setNotice({
      severity: 'error', text: `Couldn't ${hide ? 'hide' : 'unhide'} it. ${errorText(error)}`,
    }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ENTRIES_KEY });
      void queryClient.invalidateQueries({ queryKey: USER_VIEW_KEYS[0] });
    },
  });
  const onSetHidden = (entry, hide) => {
    setNotice(null);
    setHidden.mutate({ entry, hide });
  };

  const items = Array.isArray(entries.data?.items) ? entries.data.items : [];
  const groups = groupEntries(items);
  let body;
  if (entries.isPending) body = <Loading text={`Loading ${name}…`} />;
  else if (entries.isError) {
    body = <LoadError text={`Couldn't load ${name}. ${errorText(entries.error)}`} onRetry={() => entries.refetch()} />;
  } else if (groups.length === 0) {
    body = <Typography sx={{ fontSize: '0.8rem' }}>{`Nothing is stored for ${name} yet. Regenerate it to draft one.`}</Typography>;
  } else {
    body = groups.map((group) => (
      <EntryGroup key={group.key} group={group} busy={setHidden.isPending} onSetHidden={onSetHidden} />
    ));
  }
  return (
    <Section
      title={`${name} in the calendar`}
      caption="The month and any weeks in it. Replaced versions are kept as history."
      action={<RefreshButton label={`Refresh ${name}`} onClick={() => entries.refetch()} />}
    >
      {body}
      {Number(entries.data?.total) > items.length && (
        <Typography sx={secondaryTextSx}>{`Showing the newest ${items.length} of ${entries.data.total} versions.`}</Typography>
      )}
      {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.text}</Alert>}
    </Section>
  );
}

MonthEntries.propTypes = { month: PropTypes.string.isRequired };

function RecentRuns({ runs }) {
  const items = Array.isArray(runs.data?.items) ? runs.data.items : [];
  const budget = remainingBudget(runs.data?.spend);
  const caption = budget
    ? `AI spend this month (UTC): ${formatUsd(budget.month.spent)} of ${formatUsd(budget.month.cap)} · `
      + `All time: ${formatUsd(budget.total.spent)} of ${formatUsd(budget.total.cap)} · `
      + `Each run: up to ${formatUsd(budget.run)}`
    : 'The monthly job, regenerations, seed loads and link checks, newest first.';
  let body;
  if (runs.isPending) body = <Box sx={{ px: 1.5 }}><Loading text="Loading runs…" /></Box>;
  // A failed refresh keeps the runs already loaded.
  else if (runs.isError && !runs.data) {
    body = (
      <Box sx={{ p: 1.2 }}>
        <LoadError text={`Couldn't load the runs. ${errorText(runs.error)}`} onRetry={() => runs.refetch()} />
      </Box>
    );
  } else {
    body = (
      <TableContainer sx={{ maxHeight: 340 }}>
        <Table stickyHeader size="small" aria-label="Recent market calendar runs">
          <TableHead>
            <TableRow>
              <TableCell>Started</TableCell>
              <TableCell>Run</TableCell>
              <TableCell>Month</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell align="right">Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary" align="center">No runs yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {items.map((run) => {
              const status = RUN_STATUSES[run.status] || { label: run.status, color: 'default' };
              const usage = Number(run.input_tokens) > 0
                ? `${Number(run.input_tokens).toLocaleString()} input and ${Number(run.output_tokens).toLocaleString()} `
                  + `output tokens, ${run.web_search_calls} web searches`
                : undefined;
              return (
                <TableRow key={run.job_run_id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatUpdated(run.started_at) || '–'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{RUN_KINDS[run.kind] || run.kind}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{shortMonthLabel(run.target_month)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={status.color} label={status.label} sx={uppercaseLabel()} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>{describeRun(run)}</TableCell>
                  <TableCell align="right" title={usage} sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {runCost(run)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
  return (
    <Section
      title="Recent runs"
      caption={caption}
      action={<RefreshButton label="Refresh runs" onClick={() => runs.refetch()} />}
      bodySx={{ p: 0, gap: 0 }}
    >
      {body}
    </Section>
  );
}

RecentRuns.propTypes = { runs: PropTypes.object.isRequired };

function BudgetLines({ runs }) {
  const budget = remainingBudget(runs.data?.spend);
  if (!budget) {
    return (
      <Typography sx={secondaryTextSx}>
        {runs.isPending
          ? 'Loading the remaining budget…'
          : 'The remaining budget couldn\'t be loaded. The server still applies the spend caps.'}
      </Typography>
    );
  }
  const low = budget.month.left < budget.run || budget.total.left < budget.run;
  return (
    <>
      <Box component="ul" aria-label="Cost and budget" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.35, fontSize: '0.8rem' }}>
        <li>{`This run costs at most ${formatUsd(budget.run)}.`}</li>
        <li>{`Left this month (UTC): ${formatUsd(budget.month.left)} of ${formatUsd(budget.month.cap)}.`}</li>
        <li>{`Left in total: ${formatUsd(budget.total.left)} of ${formatUsd(budget.total.cap)}.`}</li>
      </Box>
      {low && (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          Less than one run&apos;s cap is left, so a cap may stop this run before it calls OpenAI. Nothing is charged then.
        </Alert>
      )}
    </>
  );
}

BudgetLines.propTypes = { runs: PropTypes.object.isRequired };

export default function MarketCalendarPanel({ onOpenSystemCredentials = null }) {
  const queryClient = useQueryClient();
  const latest = latestCompletedMonth();
  const [month, setMonth] = useState(latest);
  const [confirming, setConfirming] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(null);

  // Read from the mutation cache, so a run started before this tab was last
  // left still shows its progress, and then its result.
  const regenerations = useMutationState({
    filters: { mutationKey: REGENERATE_KEY },
    select: (mutation) => mutation.state,
  });
  const inFlight = regenerations.find((state) => state.status === 'pending') || null;
  const last = regenerations[regenerations.length - 1] || null;

  const runs = useQuery({
    queryKey: RUNS_KEY,
    queryFn: ({ signal }) => callBackend(`admin/market-calendar/job-runs?limit=${RUN_LIMIT}`, 'GET', null, { signal }),
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: inFlight ? PROGRESS_POLL_MS : false,
  });
  const credentials = useQuery(systemCredentialsQuery);
  const regenerate = useMutation({
    mutationKey: REGENERATE_KEY,
    mutationFn: (target) => callBackend('admin/market-calendar/regenerate', 'POST', { month: target }),
    gcTime: RESULT_KEEP_MS,
    onSettled: () => {
      [ADMIN_KEY, ...USER_VIEW_KEYS].forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey });
      });
    },
  });

  const openAiKey = (credentials.data?.credentials || []).find((item) => item.id === 'openai-api-key');
  const keyMissing = KEY_MISSING_STATUSES.has(openAiKey?.status);
  const [year, monthOfYear] = month.split('-');
  const latestYear = Number(latest.slice(0, 4));
  const years = Array.from({ length: latestYear - FIRST_YEAR + 1 }, (_, index) => String(latestYear - index));
  // The server regenerates completed months only.
  const pick = (value) => setMonth(value > latest ? latest : value);

  const inFlightRow = inFlight
    ? (runs.data?.items || []).find((run) => run.kind === 'regenerate' && run.status === 'running'
      && run.target_month === `${inFlight.variables}-01`)
    : null;
  const outcome = last && last.status !== 'pending' && last.submittedAt !== dismissedAt
    ? (last.status === 'success'
      ? describeRegeneration(last.data, last.variables)
      : describeRegenerationError(last.error, last.variables))
    : null;
  const openKeyButton = onOpenSystemCredentials && (
    <Button size="small" variant="outlined" onClick={onOpenSystemCredentials} sx={{ ...smallButtonSx, mt: 0.75 }}>
      Open System Credentials
    </Button>
  );

  // The dialog stays clickable while it fades out: a second click must not send a second run.
  const confirm = () => {
    if (!confirming || inFlight) return;
    setConfirming(false);
    regenerate.mutate(month);
  };

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Section
        title="Regenerate a month"
        caption={'OpenAI drafts the month again with web search, and every cited page is checked. With 2 or '
          + 'more verified events, the new version replaces what users see; the old one is kept as history.'}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <TextField
            select
            size="small"
            label="Month"
            value={monthOfYear}
            onChange={(event) => pick(`${year}-${event.target.value}`)}
            sx={{ minWidth: 150 }}
          >
            {MONTH_NAMES.map((monthName, index) => (
              <MenuItem key={monthName} value={pad(index + 1)} disabled={`${year}-${pad(index + 1)}` > latest}>
                {monthName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Year"
            value={year}
            onChange={(event) => pick(`${event.target.value}-${monthOfYear}`)}
            sx={{ minWidth: 110 }}
          >
            {years.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <Button
            variant="contained"
            size="small"
            disabled={Boolean(inFlight) || keyMissing}
            onClick={() => setConfirming(true)}
            sx={smallButtonSx}
          >
            Regenerate month
          </Button>
        </Box>
        {keyMissing && (
          <Alert severity="warning">
            {openAiKey.status === 'not_configured'
              ? 'This environment isn\'t configured for an OpenAI key, so months can\'t be regenerated here.'
              : 'No OpenAI key yet. Add one under System Credentials to regenerate a month.'}
            {openAiKey.status !== 'not_configured' && openKeyButton && <Box>{openKeyButton}</Box>}
          </Alert>
        )}
        {inFlight && (
          <Box role="status" sx={{ display: 'grid', gap: 0.6 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
              {`Regenerating ${monthLabel(inFlight.variables)}: ${runPhase(inFlightRow)}…`}
            </Typography>
            <LinearProgress aria-label={`Regenerating ${monthLabel(inFlight.variables)}`} />
            <Typography sx={secondaryTextSx}>
              {`Started ${formatUpdated(inFlight.submittedAt)}. This usually takes 1 to 3 minutes. The run carries on `
                + 'if you leave this tab, and its result shows under Recent runs.'}
            </Typography>
          </Box>
        )}
        {outcome && (
          <Alert severity={outcome.severity} onClose={() => setDismissedAt(last.submittedAt)}>
            {outcome.text}
            {outcome.needsKey && openKeyButton && <Box>{openKeyButton}</Box>}
          </Alert>
        )}
      </Section>

      <MonthEntries key={month} month={month} />
      <RecentRuns runs={runs} />

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="market-calendar-regenerate-title"
      >
        <DialogTitle id="market-calendar-regenerate-title" sx={{ fontSize: '0.95rem' }}>
          {`Regenerate ${monthLabel(month)}?`}
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.25, pt: 1 }}>
          <Typography sx={{ fontSize: '0.8rem' }}>
            {`OpenAI drafts ${monthLabel(month)} again with web search, then every cited page is checked. If at `
              + 'least 2 events verify, the new version replaces what users see now and the current version is '
              + 'kept as history. Otherwise the current version stays.'}
          </Typography>
          <BudgetLines runs={runs} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={!confirming || Boolean(inFlight)}>{`Regenerate ${monthLabel(month)}`}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

MarketCalendarPanel.propTypes = {
  onOpenSystemCredentials: PropTypes.func,
};
